/**
 * Umschalttag Billbee → StoreOS (N2/R71, Runbook 08 Schritt 4.3), Seite /einstellungen/umschaltung.
 *
 *  1. Vorschau (Leitung): zu T0 zeigt StoreOS, welche Aufträge aus dem Parallelbetrieb Archiv werden (Billbee
 *     versendet bzw. hat versendet) und welche StoreOS übernimmt (Bestätigung der Leitung nötig).
 *  2. Durchführen (Admin): doppelte Bestätigung (Vorschau geprüft + Wort UMSCHALTEN), danach das Ergebnis.
 *  3. Bestätigen (Leitung): je übernommenem Auftrag StoreOS (Klärfall gelöst) oder Billbee (Archiv), mit Grund.
 *     Derselbe Dialog steht im Auftragsdetail an der Sendung mit dem Klärfall „UMSCHALTUNG …“.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UmschaltEntscheidung } from '@storeos/contracts';
import { umschaltBestaetigen, umschaltDurchfuehren, umschaltVorschau, type UmschaltErgebnisJson, type UmschaltPlanJson } from '../api/umschaltung';
import { datumZeit, SENDUNG_TEXT } from '../components/format';
import { SendungBadge } from '../components/Status';
import { Darf, Dialog, DialogFormular, Feld, Kachel, Karte, Seitenkopf, Tabelle, useDarf, useToast, Zustand } from '../components/ui';

type Zeile = UmschaltPlanJson['archiv'][number];
const WORT = 'UMSCHALTEN';

/** Jetzt als Wert für <input type="datetime-local" step="1"> (Ortszeit, sekundengenau: T0 trennt Bestellungen derselben Minute). */
export function lokalFuerEingabe(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
const alsIso = (lokal: string): string | null => { const d = new Date(lokal); return Number.isNaN(d.getTime()) ? null : d.toISOString(); };

export function UmschaltungSeite() {
  const darf = useDarf();
  const qc = useQueryClient();
  const toast = useToast();
  const [t0Lokal, setT0Lokal] = useState(() => lokalFuerEingabe());
  const [t0, setT0] = useState<string | null>(null);
  const [durchfuehren, setDurchfuehren] = useState(false);
  const [ergebnis, setErgebnis] = useState<UmschaltErgebnisJson | null>(null);
  const [bestaetigung, setBestaetigung] = useState<Zeile | null>(null);
  const plan = useQuery({ queryKey: ['umschaltung', t0], queryFn: () => umschaltVorschau(t0!), enabled: !!t0 });
  const neuLaden = () => { void qc.invalidateQueries({ queryKey: ['umschaltung'] }); void qc.invalidateQueries({ queryKey: ['auftraege'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }); };

  const spalten = (mitGrund = true) => [
    { kopf: 'Bestellnr.', zelle: (z: Zeile) => <Link to={`/auftraege/${z.order_id}`} className="mono">{z.bestellnummer}</Link> },
    { kopf: 'Bestellt', zelle: (z: Zeile) => datumZeit(z.bestellt_at) },
    { kopf: 'Bezahlt', zelle: (z: Zeile) => datumZeit(z.bezahlt_at) },
    { kopf: 'Kanal', zelle: (z: Zeile) => <span className="mono">{z.kanal_status}</span> },
    { kopf: 'Sendungen', zelle: (z: Zeile) => <span className="badge-reihe">{z.sendungen.map((s) => <SendungBadge key={s.id} status={s.status} />)}{!z.sendungen.length && <span className="hinweis">keine</span>}</span> },
    ...(mitGrund ? [{ kopf: 'Warum', zelle: (z: Zeile) => <span className="kuerzen">{z.grund}</span> }] : []),
  ];

  return (
    <section>
      <Seitenkopf titel="Umschalttag Billbee → StoreOS" unter="Legt für jeden Auftrag aus dem Parallelbetrieb fest, wer ihn versendet. Erst Vorschau, dann Durchführen (Admin), dann je Auftrag bestätigen (Leitung).">
        <Link className="knopf-zweit" to="/einstellungen">Zurück zu den Einstellungen</Link>
      </Seitenkopf>
      <Karte titel="1. Vorschau zum Umschaltzeitpunkt T0">
        <p className="hinweis">
          Vor T0 bezahlt oder im Shop schon „completed“ → <strong>Billbee</strong> versendet: der Auftrag wird Archiv (offene Sendungen storniert, keine Rechnung aus StoreOS).
          Bei T0 unbezahlt oder erst danach bezahlt → <strong>StoreOS</strong> übernimmt, sobald du es je Auftrag bestätigst (bis dahin Klärfall „UMSCHALTUNG“).
          Nichts davon ändert etwas im Shop.
        </p>
        <form className="formular zeile" onSubmit={(e) => { e.preventDefault(); const iso = alsIso(t0Lokal); if (!iso) { toast('T0 ist kein gültiger Zeitpunkt', 'fehler'); return; } setErgebnis(null); setT0(iso); }}>
          <Feld label="T0 (Ortszeit)"><input type="datetime-local" step={1} value={t0Lokal} onChange={(e) => setT0Lokal(e.target.value)} data-testid="um-t0" required /></Feld>
          <button type="submit" className="knopf" data-testid="um-vorschau">Vorschau laden</button>
        </form>
        <Zustand laden={plan.isLoading} fehler={plan.error}>
          {plan.data && (
            <div data-testid="um-plan" data-t0={plan.data.t0}>
              <div className="kacheln">
                <Kachel wert={<span data-testid="um-anzahl-archiv">{plan.data.archiv.length}</span>} text="Archiv (Billbee versendet)" klasse="k-storniert" />
                <Kachel wert={<span data-testid="um-anzahl-uebernahme">{plan.data.uebernahme.length}</span>} text="StoreOS übernimmt (Bestätigung nötig)" klasse="k-klaerfall" />
                <Kachel wert={<span data-testid="um-anzahl-offen">{plan.data.offen_bestaetigung.length}</span>} text="Schon umgeschaltet, Bestätigung offen" klasse="k-bereit" />
              </div>
              <h3>Archiv: Billbee versendet ({plan.data.archiv.length})</h3>
              <Tabelle testid="um-archiv" zeilen={plan.data.archiv} schluessel={(z) => z.order_id} leer="Kein Auftrag geht ins Archiv" spalten={spalten()} />
              <h3>StoreOS übernimmt nach Bestätigung ({plan.data.uebernahme.length})</h3>
              <Tabelle testid="um-uebernahme" zeilen={plan.data.uebernahme} schluessel={(z) => z.order_id} leer="Kein Auftrag zur Übernahme" spalten={spalten()} />
            </div>
          )}
        </Zustand>
      </Karte>

      <Darf rolle="admin">
        <Karte titel="2. Umschaltung durchführen (Admin)" klasse={plan.data && (plan.data.archiv.length || plan.data.uebernahme.length) ? 'alarm' : ''}>
          <p className="hinweis">Unumkehrbar für die Archiv-Aufträge: ihre offenen Sendungen werden storniert (mit Label: Alarm-Klärfall), sie bekommen nie eine Rechnung aus StoreOS. Übernommene Aufträge warten als Klärfall auf deine Bestätigung. Wiederholbar: schon umgeschaltete Aufträge bleiben unberührt.</p>
          <button type="button" className="knopf-gefahr" disabled={!plan.data} onClick={() => setDurchfuehren(true)} data-testid="um-durchfuehren">Umschaltung zu T0 durchführen …</button>
          {ergebnis && (
            <dl className="werte" data-testid="um-ergebnis">
              <dt>T0</dt><dd>{datumZeit(ergebnis.t0)}</dd>
              <dt>Archiv (Billbee)</dt><dd data-testid="um-erg-archiviert">{ergebnis.archiviert} Auftrag/Aufträge, {ergebnis.sendungen_geschlossen} Sendung(en) geschlossen</dd>
              <dt>Übernommen (StoreOS)</dt><dd data-testid="um-erg-uebernommen">{ergebnis.uebernommen} Auftrag/Aufträge, Bestätigung unten bzw. im Auftrag</dd>
            </dl>
          )}
        </Karte>
      </Darf>

      {plan.data && (
        <Karte titel={`3. Bestätigung der Leitung (${plan.data.offen_bestaetigung.length} offen)`}>
          <p className="hinweis">Je Auftrag: <strong>StoreOS versendet</strong> (Klärfall gelöst, normaler Ablauf) oder <strong>Billbee versendet</strong> (Archiv, Sendungen storniert). Der Grund steht im Protokoll.</p>
          <Tabelle testid="um-offen" zeilen={plan.data.offen_bestaetigung} schluessel={(z) => z.order_id} leer="Nichts zu bestätigen"
            spalten={[...spalten(false), { kopf: '', zelle: (z: Zeile) => (darf('leitung') ? <button type="button" className="knopf klein" onClick={() => setBestaetigung(z)} data-testid="um-bestaetigen">Bestätigen …</button> : null) }]} />
        </Karte>
      )}

      <Dialog offen={durchfuehren} titel="Umschaltung durchführen" onSchliessen={() => setDurchfuehren(false)}>
        {durchfuehren && plan.data && t0 && (
          <DurchfuehrenFormular plan={plan.data} onAbbrechen={() => setDurchfuehren(false)} onFertig={async () => {
            const r = await umschaltDurchfuehren(t0);
            setErgebnis(r); setDurchfuehren(false);
            toast(`Umschaltung durchgeführt: ${r.archiviert} Archiv, ${r.uebernommen} übernommen`);
            neuLaden();
          }} />
        )}
      </Dialog>
      <UmschaltBestaetigenDialog zeile={bestaetigung ? { orderId: bestaetigung.order_id, bestellnummer: bestaetigung.bestellnummer, sendungen: bestaetigung.sendungen.map((s) => SENDUNG_TEXT[s.status]) } : null}
        onSchliessen={() => setBestaetigung(null)} onFertig={(text) => { setBestaetigung(null); toast(text); neuLaden(); }} />
    </section>
  );
}

function DurchfuehrenFormular({ plan, onAbbrechen, onFertig }: { plan: UmschaltPlanJson; onAbbrechen: () => void; onFertig: () => Promise<void> }) {
  const [geprueft, setGeprueft] = useState(false);
  const [wort, setWort] = useState('');
  const [schritt, setSchritt] = useState<1 | 2>(1);
  return (
    <DialogFormular absendenText={schritt === 1 ? 'Weiter' : 'Ja, jetzt umschalten'} gefahr={schritt === 2} onAbbrechen={onAbbrechen} onAbsenden={async () => {
      if (!geprueft) throw new Error('Bitte bestätigen, dass du die Vorschau geprüft hast.');
      if (schritt === 1) { setSchritt(2); return; }
      if (wort.trim().toUpperCase() !== WORT) throw new Error(`Zur zweiten Bestätigung das Wort ${WORT} eintippen.`);
      await onFertig();
    }}>
      <div className="warn-box">
        <strong>T0 {datumZeit(plan.t0)}: {plan.archiv.length} Auftrag/Aufträge werden Archiv (Billbee), {plan.uebernahme.length} übernimmt StoreOS nach Bestätigung.</strong>
        <span>Archiv heißt: offene Sendungen storniert, Reservierung frei, keine Rechnung aus StoreOS. Das lässt sich nicht zurücknehmen.</span>
      </div>
      <label className="inline"><input type="checkbox" checked={geprueft} onChange={(e) => setGeprueft(e.target.checked)} data-testid="um-geprueft" /> Ich habe die Vorschau geprüft, T0 stimmt mit dem Runbook überein.</label>
      {schritt === 2 && (
        <Feld label={`Zweite Bestätigung: ${WORT} eintippen`}><input value={wort} onChange={(e) => setWort(e.target.value)} data-testid="um-wort" autoFocus autoComplete="off" /></Feld>
      )}
    </DialogFormular>
  );
}

export interface UmschaltZeileKurz { orderId: string; bestellnummer: string; sendungen: string[] }

/** Bestätigung je Auftrag (Leitung): StoreOS oder Billbee, Grund Pflicht, Bestätigung per Häkchen. */
export function UmschaltBestaetigenDialog({ zeile, onSchliessen, onFertig }: { zeile: UmschaltZeileKurz | null; onSchliessen: () => void; onFertig: (text: string) => void }) {
  return (
    <Dialog offen={!!zeile} titel={`Umschaltung bestätigen: Auftrag ${zeile?.bestellnummer ?? ''}`} onSchliessen={onSchliessen}>
      {zeile && <BestaetigenFormular key={zeile.orderId} zeile={zeile} onAbbrechen={onSchliessen} onFertig={onFertig} />}
    </Dialog>
  );
}

function BestaetigenFormular({ zeile, onAbbrechen, onFertig }: { zeile: UmschaltZeileKurz; onAbbrechen: () => void; onFertig: (text: string) => void }) {
  const [entscheidung, setEntscheidung] = useState<UmschaltEntscheidung | ''>('');
  const [grund, setGrund] = useState('');
  const [bestaetigt, setBestaetigt] = useState(false);
  return (
    <DialogFormular absendenText={entscheidung === 'billbee' ? 'Billbee versendet: Auftrag ins Archiv' : 'StoreOS versendet: Klärfall lösen'} gefahr={entscheidung === 'billbee'} onAbbrechen={onAbbrechen}
      onAbsenden={async () => {
        if (!entscheidung) throw new Error('Entscheidung wählen: StoreOS oder Billbee.');
        if (grund.trim().length < 5) throw new Error('Grund ist Pflicht (mindestens 5 Zeichen), z. B. „in Billbee geprüft: nicht versandt“.');
        if (!bestaetigt) throw new Error('Bitte bestätigen, dass du in Billbee nachgesehen hast.');
        const r = await umschaltBestaetigen([zeile.orderId], entscheidung, grund.trim());
        if (!r.bestaetigt.includes(zeile.orderId)) throw new Error('Der Auftrag wartet nicht mehr auf eine Bestätigung (schon entschieden?).');
        onFertig(entscheidung === 'storeos' ? `Auftrag ${zeile.bestellnummer}: StoreOS versendet, Klärfall gelöst` : `Auftrag ${zeile.bestellnummer}: Billbee versendet, Auftrag im Archiv`);
      }}>
      <p>Der Auftrag kam im Parallelbetrieb und war bei T0 unbezahlt bzw. wurde erst danach bezahlt. Prüfe in Billbee, ob er dort schon versandt wurde.</p>
      {zeile.sendungen.length > 0 && <p className="hinweis">Sendungen in StoreOS: {zeile.sendungen.join(', ')}</p>}
      <Feld label="Wer versendet? (Pflicht)">
        <select value={entscheidung} onChange={(e) => setEntscheidung(e.target.value as UmschaltEntscheidung | '')} data-testid="um-entscheidung" autoFocus>
          <option value="">– wählen –</option>
          <option value="storeos">StoreOS: Billbee hat ihn nicht versandt, StoreOS packt und berechnet ihn</option>
          <option value="billbee">Billbee: dort schon versandt oder in Arbeit, StoreOS archiviert (Sendungen storniert, keine Rechnung)</option>
        </select>
      </Feld>
      <Feld label="Grund (Pflicht)"><input value={grund} onChange={(e) => setGrund(e.target.value)} data-testid="um-grund" placeholder="z. B. in Billbee geprüft: nicht versandt" /></Feld>
      <label className="inline"><input type="checkbox" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} data-testid="um-haken" /> Ich habe in Billbee nachgesehen; ein Doppelversand ist ausgeschlossen.</label>
    </DialogFormular>
  );
}
